/**
 * Minimal, isomorphic SVG sanitizer.
 *
 * Authors reach this field only through the authenticated editor, so this is
 * defence in depth rather than the primary control — but the markup ends up on
 * public pages, and an icon field has no legitimate reason to carry script. The
 * `EmbedCode` block deliberately does execute raw HTML; this one must not.
 *
 * String-based on purpose: it has to produce the same output during the Gatsby
 * build (no DOM) and in the browser. It is deliberately conservative — anything
 * it cannot recognise is dropped rather than passed through.
 */

/** Elements that can execute or load, none of which an icon needs. */
const FORBIDDEN_TAGS = [
  'script',
  'foreignObject',
  'iframe',
  'object',
  'embed',
  'audio',
  'video',
  'animate',
  'set',
  'handler',
  'listener',
]

const stripTag = (svg: string, tag: string): string =>
  svg
    // paired form, including any content
    .replace(new RegExp(`<\\s*${tag}\\b[\\s\\S]*?<\\s*/\\s*${tag}\\s*>`, 'gi'), '')
    // self-closing / unterminated form
    .replace(new RegExp(`<\\s*${tag}\\b[^>]*/?>`, 'gi'), '')

export function sanitizeSvg(raw: string | undefined): string {
  if (!raw) return ''
  let svg = raw

  // Comments can hide markup from the naive passes below.
  svg = svg.replace(/<!--[\s\S]*?-->/g, '')
  svg = svg.replace(/<!\[CDATA\[[\s\S]*?\]\]>/g, '')
  svg = svg.replace(/<\?[\s\S]*?\?>/g, '') // XML prolog
  svg = svg.replace(/<!DOCTYPE[^>]*>/gi, '')

  for (const tag of FORBIDDEN_TAGS) svg = stripTag(svg, tag)

  // Event handlers: on* attributes, quoted or bare.
  svg = svg.replace(/\son[a-z-]+\s*=\s*"[^"]*"/gi, '')
  svg = svg.replace(/\son[a-z-]+\s*=\s*'[^']*'/gi, '')
  svg = svg.replace(/\son[a-z-]+\s*=\s*[^\s>]+/gi, '')

  // javascript:/data: URLs in any href or url() reference.
  svg = svg.replace(
    /\s(?:xlink:)?href\s*=\s*("|')?\s*(?:javascript|data)\s*:[^"'>\s]*\1?/gi,
    '',
  )
  svg = svg.replace(/url\(\s*['"]?\s*javascript:[^)]*\)/gi, 'none')

  // Keep only from the first <svg ...> to its matching close: strips anything a
  // designer's export tool bolted on around it.
  const open = svg.search(/<\s*svg\b/i)
  if (open === -1) return ''
  const close = svg.toLowerCase().lastIndexOf('</svg>')
  if (close === -1) return ''
  return svg.slice(open, close + '</svg>'.length).trim()
}

/**
 * Make an SVG take its colour from CSS `color` and scale to its box.
 *
 * Pasted icons almost always hardcode `fill="#333"`, which would make the Color
 * field do nothing. Fixed `width`/`height` attributes are dropped for the same
 * reason: they would win over the Size field.
 */
export function normalizeSvg(svg: string, forceCurrentColor: boolean): string {
  if (!svg) return ''
  let out = svg

  out = out.replace(/<\s*svg\b([^>]*)>/i, (_m, attrs: string) => {
    let a = attrs
      .replace(/\swidth\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/\sheight\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    if (!/\bpreserveAspectRatio\s*=/i.test(a)) {
      a += ' preserveAspectRatio="xMidYMid meet"'
    }
    return `<svg${a} width="100%" height="100%" focusable="false">`
  })

  if (forceCurrentColor) {
    out = out.replace(
      /(fill|stroke)\s*=\s*("|')(?!none\b|url\(|currentColor\b)[^"']*\2/gi,
      '$1="currentColor"',
    )
    // Same values expressed inline in a style attribute.
    out = out.replace(
      /(fill|stroke)\s*:\s*(?!none\b|url\(|currentColor\b)[^;"']+/gi,
      '$1:currentColor',
    )
  }

  return out
}
